import React, { useEffect, useMemo, useState } from "react";
import { StatCard } from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TrendingUp, TrendingDown, Users, Scale, X, PieChart, ChevronLeft } from "lucide-react";
import SavedViewsRow from "@/components/SavedViewsRow";
import {
  fmt, invoiceRevenueINR, invoiceOutstandingINR, OUTSTANDING_INVOICE_STATUSES, isCountableExpense, expenseAmountINR, normalizePersonKey, expensePersonName, PERIOD_GRANULARITIES, periodRange, rowBucketDate,
  monthPickerOptions, quarterPickerOptions, currentMonthValue, currentQuarterValue, fmtLakhs,
} from "@/lib/finance";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from "recharts";

const OUTSTANDING_STATUSES = OUTSTANDING_INVOICE_STATUSES;
const RECEIVED_STATUSES = ["received", "paid"];
const RECEIVED_COLOR = "#22C55E";
const OUTSTANDING_COLOR = "#F59E0B";

// Feb 2026 — Master Dynamic Analytics Engine: 5 graph modes, each driving
// the SAME singular full-width BarChart. `series` names the mode's own
// (non-stacked) dataKey; `ledgerTab` is where a bar click drills into.
const GRAPH_MODES = [
  { id: "revenue", label: "Revenue Trends", icon: TrendingUp, series: "Revenue", color: "#22C55E", ledgerTab: "revenue-ledger" },
  { id: "expense", label: "Expense Trends", icon: TrendingDown, series: "Expense", color: "#F54800", ledgerTab: "expenses-ledger" },
  { id: "personnel", label: "Personnel Burn", icon: Users, series: "Spend", color: "#F54800", ledgerTab: "expenses-ledger" },
  { id: "category_burn", label: "Category Burn", icon: PieChart, series: "Spend", color: "#F54800", ledgerTab: "expenses-ledger" },
  { id: "profitability", label: "Project Profitability", icon: Scale, series: null, color: null, ledgerTab: null },
];

// Feb 2026 — Interactive Sub-category Stacking: a fixed, deterministic
// studio-orange-anchored palette for dynamic stack segments (unknown
// count/names until render time) — same segment name always gets the
// same color across every chart on the page.
const STACK_PALETTE = ["#F54800", "#0EA5E9", "#16A34A", "#9333EA", "#EAB308", "#DC2626", "#14B8A6", "#64748B", "#FB923C", "#0F172A"];
function colorForSegment(name) {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return STACK_PALETTE[h % STACK_PALETTE.length];
}

// bucket boundaries for the trend graph — adapts to granularity, and to the
// MoM/QoQ Time Navigator's picked anchor month/quarter for monthly/quarterly.
function buildBuckets(granularity, anchor = {}) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const buckets = [];
  if (granularity === "daily") {
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now); d.setDate(now.getDate() - i);
      const ds = iso(d);
      buckets.push({ key: ds, label: d.toLocaleDateString("en-US", { day: "numeric", month: "short" }), from: ds, to: ds });
    }
  } else if (granularity === "weekly") {
    for (let i = 7; i >= 0; i--) {
      const end = new Date(now); end.setDate(now.getDate() - i * 7);
      const start = new Date(end); start.setDate(end.getDate() - 6);
      buckets.push({ key: iso(start), label: `${start.getDate()} ${start.toLocaleDateString("en-US", { month: "short" })}`, from: iso(start), to: iso(end) });
    }
  } else if (granularity === "quarterly") {
    const [ay, aq] = anchor.quarter ? anchor.quarter.split("-Q").map(Number) : [now.getFullYear(), Math.floor(now.getMonth() / 3) + 1];
    const anchorIdx = ay * 4 + (aq - 1);
    for (let i = 7; i >= 0; i--) {
      const qIdx = anchorIdx - i;
      const year = Math.floor(qIdx / 4);
      const qm = ((qIdx % 4) + 4) % 4;
      const start = new Date(year, qm * 3, 1);
      const end = new Date(year, qm * 3 + 3, 0);
      buckets.push({ key: `${year}-Q${qm + 1}`, label: `Q${qm + 1} ${year}`, from: iso(start), to: iso(end) });
    }
  } else if (granularity === "ytd") {
    const curMonth = now.getMonth();
    for (let m = 0; m <= curMonth; m++) {
      const start = new Date(now.getFullYear(), m, 1);
      const end = new Date(now.getFullYear(), m + 1, 0);
      buckets.push({ key: `${now.getFullYear()}-${pad(m + 1)}`, label: start.toLocaleString("en-US", { month: "short" }), from: iso(start), to: iso(m === curMonth ? now : end) });
    }
  } else if (granularity === "monthly") {
    const [my, mm] = anchor.month ? anchor.month.split("-").map(Number) : [now.getFullYear(), now.getMonth() + 1];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(my, mm - 1 - i, 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      buckets.push({ key: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`, label: d.toLocaleString("en-US", { month: "short", year: "numeric" }), from: iso(d), to: iso(end) });
    }
  }
  return buckets;
}

// YoY Comparison: shift an ISO date string back N years, keeping month/day
// (Feb 29 safely clamps to Feb 28 on non-leap years).
function shiftYears(isoStr, delta) {
  const [y, m, d] = isoStr.split("-").map(Number);
  const shifted = new Date(y + delta, m - 1, d);
  if (shifted.getMonth() !== m - 1) shifted.setDate(0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}`;
}

/* ---------------------------------------------------------------------------
   Finance Analytics Panel — the Master Dynamic Analytics Engine extracted
   from pages/Finance.jsx (Feb 2026 split). Owns ALL mode/filter/time-
   navigator/stacking state; talks to the parent page via two callbacks:
     - onFilterChange(globalPreset) — fired whenever the resolved filters
       change, so the parent can pass them into the ledger tabs below.
     - onNavigate(tab, quickFilter) — fired when a bar/segment/StatCard is
       clicked, so the parent can switch its own Tabs + scroll to them.
--------------------------------------------------------------------------- */
export default function FinanceAnalyticsPanel({ invoices, expenses, projectsList, onFilterChange, onNavigate, onWindowChange }) {
  // Master Dynamic Analytics Engine state.
  const [mode, setMode] = useState("revenue");
  const [granularity, setGranularity] = useState("monthly");
  const [pickedMonth, setPickedMonth] = useState(currentMonthValue());
  const [pickedQuarter, setPickedQuarter] = useState(currentQuarterValue());
  const [gProjectId, setGProjectId] = useState("");
  const [gServiceType, setGServiceType] = useState("");   // Revenue Trends dimension
  const [gCategory, setGCategory] = useState("");          // Expense Trends dimension
  const [gSubCategory, setGSubCategory] = useState("");    // Expense Trends dimension
  const [gPerson, setGPerson] = useState("");               // Personnel Burn dimension
  const [catDrill, setCatDrill] = useState(null);            // Category Burn: {department} once drilled in, else null (top level)
  const [drillRange, setDrillRange] = useState(null); // {from,to,label} — one-off graph-bar drilldown
  const [showYoY, setShowYoY] = useState(false); // overlay last-year-same-period bars

  useEffect(() => { setDrillRange(null); setCatDrill(null); }, [granularity, mode]);
  // Dependent Logic — a Project change invalidates any mode-specific
  // dimension picked from the PREVIOUS project's data.
  useEffect(() => { setGServiceType(""); setGCategory(""); setGSubCategory(""); setGPerson(""); setCatDrill(null); }, [gProjectId]);

  // Feb 2026 — Interactive Sub-category Stacking: once ANY cross-cutting
  // filter narrows the view down (Project, Main Category/Department via
  // Category Burn's drill, or Service Type), every trend/comparison chart
  // pivots from a flat total bar into a stacked breakdown — Revenue stacks
  // by Service Type, Expense stacks by Sub-category (Category Burn itself
  // is excluded — its own drill-down already IS that breakdown). Also
  // triggers as soon as a Main Category is picked in Expense mode (before
  // narrowing to a specific Sub-category), so picking "Fees & Charges"
  // instantly shows its Sub-category breakdown.
  const stackingActive = !!(gProjectId || catDrill?.department || gServiceType || (mode === "expense" && gCategory && !gSubCategory));

  // Dependent Logic — Category / Service Type / Sub-category / Person
  // dropdown OPTIONS are scoped to the selected Project's own data only.
  const invoicesForProject = useMemo(() => gProjectId ? invoices.filter((i) => i.project_id === gProjectId) : invoices, [invoices, gProjectId]);
  const expensesForProject = useMemo(() => gProjectId ? expenses.filter((e) => e.project_id === gProjectId) : expenses, [expenses, gProjectId]);
  const serviceTypeChoices = useMemo(() => [...new Set(invoicesForProject.map((i) => i.service_type).filter(Boolean))].sort(), [invoicesForProject]);
  const categoryChoices = useMemo(() => [...new Set(expensesForProject.map((e) => e.category).filter(Boolean))].sort(), [expensesForProject]);
  const subCategoryChoices = useMemo(() => {
    const scoped = gCategory ? expensesForProject.filter((e) => e.category === gCategory) : expensesForProject;
    return [...new Set(scoped.map((e) => e.sub_category).filter(Boolean))].sort();
  }, [expensesForProject, gCategory]);

  // Rows scoped by Project (StatCards / ledgers below stay stable regardless
  // of which graph MODE is active) vs. rows scoped by Project + the active
  // mode's own dimension (feeds the trend graph itself).
  const invoicesByProject = useMemo(() => invoices.filter((i) => !gProjectId || i.project_id === gProjectId), [invoices, gProjectId]);
  const expensesByProject = useMemo(() => expenses.filter((e) => !gProjectId || e.project_id === gProjectId), [expenses, gProjectId]);
  const invoicesByDims = useMemo(() => invoicesByProject.filter((i) => !gServiceType || i.service_type === gServiceType), [invoicesByProject, gServiceType]);
  // Feb 2026 — Trend Filtering fix: this fed the Expense Trend bars AND the
  // sub-category stacking segments with EVERY expense row (drafts, rejected,
  // Internal Transfers, Credit Card Bill/Payment settlements) — none of the
  // "countable" rules the StatCards already use. Apply `isCountableExpense`
  // here once so both derived views agree with the rest of the app.
  const expensesByCategoryDims = useMemo(() => expensesByProject.filter((e) =>
    isCountableExpense(e) && (!gCategory || e.category === gCategory) && (!gSubCategory || e.sub_category === gSubCategory)
  ), [expensesByProject, gCategory, gSubCategory]);

  // Feb 2026 — Expense Trend Cleanup: expenses with no date at all would
  // otherwise silently vanish from every bucket (nothing to compare
  // against `from`/`to`) — surface that instead of hiding it.
  const undatedExpenseCount = useMemo(() => expensesByProject.filter((e) => !rowBucketDate(e)).length, [expensesByProject]);

  // Time Navigator's anchor — MoM ("Sep 2026") / QoQ ("Q3 2026") pickers only
  // apply when Monthly / Quarterly granularity is active.
  const anchor = useMemo(() => (
    granularity === "monthly" ? { month: pickedMonth } : granularity === "quarterly" ? { quarter: pickedQuarter } : {}
  ), [granularity, pickedMonth, pickedQuarter]);
  const rollingWindow = useMemo(() => periodRange(granularity, anchor), [granularity, anchor]);
  const effectiveWindow = drillRange || rollingWindow;

  const inWindow = (row, win) => {
    const d = rowBucketDate(row);
    return d && d >= win.from && d <= win.to;
  };
  const invoicesWindowed = useMemo(() => invoicesByProject.filter((r) => inWindow(r, effectiveWindow)), [invoicesByProject, effectiveWindow]);
  const expensesWindowed = useMemo(() => expensesByProject.filter((r) => inWindow(r, effectiveWindow)), [expensesByProject, effectiveWindow]);

  const totals = useMemo(() => {
    const received = invoicesWindowed.reduce((s, i) => s + invoiceRevenueINR(i), 0);
    const receivedRows = invoicesWindowed.filter((i) => ["received", "paid"].includes((i.status || "").toLowerCase()));
    const outstandingRows = invoicesWindowed.filter((i) => OUTSTANDING_STATUSES.includes((i.status || "").toLowerCase()));
    const outstanding = outstandingRows.reduce((s, i) => s + invoiceOutstandingINR(i), 0);
    const expense = expensesWindowed.filter(isCountableExpense).reduce((s, e) => s + expenseAmountINR(e), 0);
    return {
      received, outstanding, expense, net: received - expense,
      revenueTotal: received + outstanding,
      invoiceCount: invoicesWindowed.length, receivedCount: receivedRows.length, outstandingCount: outstandingRows.length,
    };
  }, [invoicesWindowed, expensesWindowed]);

  const activeGraphMode = GRAPH_MODES.find((m) => m.id === mode);

  // Interactive Sub-category Stacking — distinct segment names present in
  // the currently-scoped rows for each mode (empty when stacking isn't
  // active, or nothing to break down).
  const revenueStackSegments = useMemo(() => (
    mode === "revenue" && stackingActive ? [...new Set(invoicesByDims.map((i) => i.service_type || "Unspecified"))].sort() : []
  ), [mode, stackingActive, invoicesByDims]);
  const expenseStackSegments = useMemo(() => (
    mode === "expense" && stackingActive ? [...new Set(expensesByCategoryDims.map((e) => e.sub_category || "Uncategorized"))].sort() : []
  ), [mode, stackingActive, expensesByCategoryDims]);

  // Trend-graph buckets — the 3 non-profitability modes. "All Time" buckets
  // by calendar year across whatever data exists in scope.
  const graphBuckets = useMemo(() => {
    if (mode === "profitability") return [];
    if (granularity === "all") {
      const rows = [...invoicesByProject, ...expensesByProject];
      const years = [...new Set(rows.map((r) => rowBucketDate(r).slice(0, 4)).filter(Boolean))].sort();
      if (years.length === 0) years.push(String(new Date().getFullYear()));
      return years.map((y) => ({ key: y, label: y, from: `${y}-01-01`, to: `${y}-12-31` }));
    }
    return buildBuckets(granularity, anchor);
  }, [mode, granularity, anchor, invoicesByProject, expensesByProject]);

  // Oct 2026 — server-side windowing: tell the Finance page the smallest
  // date range that covers the selected period, every trend bucket, and
  // (when YoY is on) last year's shadow. "All Time" = null = whole ledger.
  const dataWindow = useMemo(() => {
    if (granularity === "all") return null;
    const froms = [effectiveWindow.from, ...graphBuckets.map((b) => b.from)];
    const tos = [effectiveWindow.to, ...graphBuckets.map((b) => b.to)];
    let from = froms.reduce((a, b) => (a < b ? a : b));
    const to = tos.reduce((a, b) => (a > b ? a : b));
    if (showYoY) from = shiftYears(from, -1);
    return { from, to };
  }, [granularity, effectiveWindow.from, effectiveWindow.to, graphBuckets, showYoY]);
  const dataWindowKey = dataWindow ? `${dataWindow.from}|${dataWindow.to}` : "all";
  useEffect(() => { onWindowChange?.(dataWindow); }, [dataWindowKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const trendGraphData = useMemo(() => {
    if (mode === "profitability" || mode === "personnel" || mode === "category_burn") return [];
    const seriesRows = mode === "revenue" ? invoicesByDims : expensesByCategoryDims;
    // REVENUE RULE 4: the Revenue series is RECEIVED ONLY — Outstanding is a
    // separate series (rendered as its own bar below), never summed in.
    const calc = mode === "revenue" ? (r) => invoiceRevenueINR(r) : (r) => expenseAmountINR(r);
    const seriesKey = activeGraphMode.series;
    const segments = mode === "revenue" ? revenueStackSegments : expenseStackSegments;
    const segmentOf = mode === "revenue" ? (r) => r.service_type || "Unspecified" : (r) => r.sub_category || "Uncategorized";
    const rowsIn = (rows, from, to) => rows.filter((r) => { const d = rowBucketDate(r); return d >= from && d <= to; });
    const sumIn = (rows, from, to) => rowsIn(rows, from, to).reduce((s, r) => s + calc(r), 0);
    return graphBuckets.map((b) => {
      const fromLY = shiftYears(b.from, -1), toLY = shiftYears(b.to, -1);
      const row = { ...b, [`${seriesKey}LY`]: Math.round(sumIn(seriesRows, fromLY, toLY)) };
      if (segments.length > 0) {
        const inBucket = rowsIn(seriesRows, b.from, b.to);
        let total = 0;
        segments.forEach((seg) => {
          const v = Math.round(inBucket.filter((r) => segmentOf(r) === seg).reduce((s, r) => s + calc(r), 0));
          row[seg] = v; total += v;
        });
        row._stackTotal = total;
      } else {
        row[seriesKey] = Math.round(sumIn(seriesRows, b.from, b.to));
        if (mode === "revenue") {
          const inBucket = rowsIn(seriesRows, b.from, b.to);
          row.Received = Math.round(inBucket.reduce((s, r) => s + invoiceRevenueINR(r), 0));
          row.Outstanding = Math.round(inBucket.reduce((s, r) => s + invoiceOutstandingINR(r), 0));
        }
      }
      return row;
    });
  }, [mode, graphBuckets, invoicesByDims, expensesByCategoryDims, activeGraphMode, revenueStackSegments, expenseStackSegments]);

  // Project Profitability — Revenue vs. Expense side-by-side per project,
  // scoped to the SAME resolved window as the StatCards (single month/
  // quarter/YTD/all-time, per the active Time Navigator). Feb 2026 —
  // Project Profitability Symmetry: Direct Costs use the SAME
  // Paid-or-Approved rule as the backend Project P&L, mirroring the
  // Revenue side's "only what's confirmed counts" rule exactly. Feb 2026 —
  // Interactive Sub-category Stacking: when active, each project's Revenue
  // bar ALSO stacks by Service Type and its Expense bar by Sub-category.
  const profitRevenueSegments = useMemo(() => {
    if (mode !== "profitability" || !stackingActive) return [];
    const list = gProjectId ? projectsList.filter((p) => p.id === gProjectId) : projectsList;
    const ids = new Set(list.map((p) => p.id));
    const rows = invoices.filter((i) => ids.has(i.project_id) && inWindow(i, effectiveWindow));
    return [...new Set(rows.map((i) => i.service_type || "Unspecified"))].sort();
  }, [mode, stackingActive, gProjectId, projectsList, invoices, effectiveWindow]);
  const profitExpenseSegments = useMemo(() => {
    if (mode !== "profitability" || !stackingActive) return [];
    const list = gProjectId ? projectsList.filter((p) => p.id === gProjectId) : projectsList;
    const ids = new Set(list.map((p) => p.id));
    const rows = expenses.filter((e) => ids.has(e.project_id) && inWindow(e, effectiveWindow) && isCountableExpense(e));
    return [...new Set(rows.map((e) => e.sub_category || "Uncategorized"))].sort();
  }, [mode, stackingActive, gProjectId, projectsList, expenses, effectiveWindow]);

  const profitabilityData = useMemo(() => {
    if (mode !== "profitability") return [];
    const winLY = { from: shiftYears(effectiveWindow.from, -1), to: shiftYears(effectiveWindow.to, -1) };
    const list = gProjectId ? projectsList.filter((p) => p.id === gProjectId) : projectsList;
    return list.map((p) => {
      const projRev = invoices.filter((i) => i.project_id === p.id && inWindow(i, effectiveWindow));
      const projExp = expenses.filter((e) => e.project_id === p.id && inWindow(e, effectiveWindow) && isCountableExpense(e));
      const rev = projRev.reduce((s, i) => s + invoiceRevenueINR(i), 0);
      const exp = projExp.reduce((s, e) => s + expenseAmountINR(e), 0);
      const revLY = invoices.filter((i) => i.project_id === p.id && inWindow(i, winLY)).reduce((s, i) => s + invoiceRevenueINR(i), 0);
      const expLY = expenses.filter((e) => e.project_id === p.id && inWindow(e, winLY) && isCountableExpense(e)).reduce((s, e) => s + expenseAmountINR(e), 0);
      const row = { key: p.id, label: p.name, Revenue: Math.round(rev), Expense: Math.round(exp), RevenueLY: Math.round(revLY), ExpenseLY: Math.round(expLY) };
      profitRevenueSegments.forEach((seg) => {
        row[`rev__${seg}`] = Math.round(projRev.filter((i) => (i.service_type || "Unspecified") === seg).reduce((s, i) => s + invoiceRevenueINR(i), 0));
      });
      profitExpenseSegments.forEach((seg) => {
        row[`exp__${seg}`] = Math.round(projExp.filter((e) => (e.sub_category || "Uncategorized") === seg).reduce((s, e) => s + expenseAmountINR(e), 0));
      });
      return row;
    }).filter((row) => row.Revenue > 0 || row.Expense > 0).sort((a, b) => (b.Revenue + b.Expense) - (a.Revenue + a.Expense));
  }, [mode, gProjectId, projectsList, invoices, expenses, effectiveWindow, profitRevenueSegments, profitExpenseSegments]);

  // Personnel Burn — Feb 2026 rebuild: one bar PER PERSON (not a time
  // trend), so it directly answers "who is spending how much". Names are
  // normalized (trimmed + lowercased) before grouping so "Priya Sharma",
  // "priya sharma " and "PRIYA SHARMA" all collapse into exactly one bar —
  // the FIRST original-cased spelling encountered is kept as the label.
  // Personnel Burn Fix — STRICTLY internal team members: `expensePersonName`
  // only resolves created_by_name / requested_by_name, NEVER `vendor`, and
  // rows with no internal owner at all (e.g. a vendor's self-uploaded PO
  // invoice) are dropped entirely rather than falling into a fake bucket.
  // Feb 2026 — Interactive Sub-category Stacking: when active, each
  // person's bar ALSO stacks by Sub-category (what they spent it on).
  const personnelStackSegments = useMemo(() => {
    if (mode !== "personnel" || !stackingActive) return [];
    const rows = expenses.filter((e) => (!gProjectId || e.project_id === gProjectId) && inWindow(e, effectiveWindow) && isCountableExpense(e) && (e.accrual_date || e.date) && expensePersonName(e));
    return [...new Set(rows.map((e) => e.sub_category || "Uncategorized"))].sort();
  }, [mode, stackingActive, expenses, gProjectId, effectiveWindow]);

  const personnelBurnData = useMemo(() => {
    if (mode !== "personnel") return [];
    const winLY = { from: shiftYears(effectiveWindow.from, -1), to: shiftYears(effectiveWindow.to, -1) };
    const byKey = new Map();
    const accumulate = (rows, win, seriesKey) => {
      rows.filter((e) => (!gProjectId || e.project_id === gProjectId) && inWindow(e, win) && isCountableExpense(e) && (e.accrual_date || e.date) && expensePersonName(e)).forEach((e) => {
        const raw = expensePersonName(e);
        const key = normalizePersonKey(raw);
        if (!byKey.has(key)) byKey.set(key, { key, label: raw, Spend: 0, SpendLY: 0 });
        const rec = byKey.get(key);
        rec[seriesKey] += expenseAmountINR(e);
        if (seriesKey === "Spend" && personnelStackSegments.length > 0) {
          const seg = e.sub_category || "Uncategorized";
          rec[seg] = (rec[seg] || 0) + expenseAmountINR(e);
        }
      });
    };
    accumulate(expenses, effectiveWindow, "Spend");
    accumulate(expenses, winLY, "SpendLY");
    return [...byKey.values()]
      .map((r) => {
        const rounded = { ...r, Spend: Math.round(r.Spend), SpendLY: Math.round(r.SpendLY) };
        personnelStackSegments.forEach((seg) => { rounded[seg] = Math.round(r[seg] || 0); });
        if (personnelStackSegments.length > 0) rounded._stackTotal = rounded.Spend;
        return rounded;
      })
      .filter((r) => r.Spend > 0 || r.SpendLY > 0)
      .sort((a, b) => b.Spend - a.Spend);
  }, [mode, gProjectId, expenses, effectiveWindow, personnelStackSegments]);

  // Category Burn — Feb 2026 / Oct 2026: drills Department → Category →
  // Sub-category entirely on the graph. `catDrill` is null at the top
  // (Departments), {department} once drilled into a department (Categories),
  // and {department, category} once drilled into a category (Sub-categories).
  // Only a Sub-category bar click leaves the chart to sync the Expenses
  // ledger. Oct 2026 — reconciliation fix: apply isCountableExpense so this
  // chart's totals match the Total Studio Expense stat card.
  const categoryBurnData = useMemo(() => {
    if (mode !== "category_burn") return [];
    const winLY = { from: shiftYears(effectiveWindow.from, -1), to: shiftYears(effectiveWindow.to, -1) };
    const byKey = new Map();
    const accumulate = (win, seriesKey) => {
      expenses.filter((e) => (!gProjectId || e.project_id === gProjectId) && inWindow(e, win) && isCountableExpense(e)).forEach((e) => {
        const dept = e.department || "Uncategorized";
        const cat = e.category || "Uncategorized";
        const sub = e.sub_category || "Uncategorized";
        if (catDrill?.department && dept !== catDrill.department) return;
        if (catDrill?.category && cat !== catDrill.category) return;
        const raw = catDrill?.category ? sub : catDrill?.department ? cat : dept;
        if (!byKey.has(raw)) byKey.set(raw, { key: raw, label: raw, Spend: 0, SpendLY: 0 });
        byKey.get(raw)[seriesKey] += expenseAmountINR(e);
      });
    };
    accumulate(effectiveWindow, "Spend");
    accumulate(winLY, "SpendLY");
    return [...byKey.values()]
      .map((r) => ({ ...r, Spend: Math.round(r.Spend), SpendLY: Math.round(r.SpendLY) }))
      .filter((r) => r.Spend > 0 || r.SpendLY > 0)
      .sort((a, b) => b.Spend - a.Spend);
  }, [mode, gProjectId, expenses, effectiveWindow, catDrill]);

  const graphData = mode === "profitability" ? profitabilityData : mode === "personnel" ? personnelBurnData : mode === "category_burn" ? categoryBurnData : trendGraphData;

  // Category Burn drill — clicking a Department bar drills to its Categories,
  // a Category bar drills to its Sub-categories (both stay on the graph);
  // only a Sub-category bar click leaves the chart to sync the Expenses
  // ledger (department + category + sub-category). Changing drill level
  // clears any stale category/sub-category ledger filter.
  const setCatLevel = (next) => { setCatDrill(next); setGCategory(""); setGSubCategory(""); };
  const goToCategoryBar = (bar) => {
    if (!catDrill) { setCatLevel({ department: bar.key }); return; }
    if (!catDrill.category) { setCatLevel({ department: catDrill.department, category: bar.key }); return; }
    setGCategory(catDrill.category);
    setGSubCategory(bar.key);
    onNavigate("expenses-ledger", null);
  };

  // Every ledger below stays in sync with the Master Filter Bar via this
  // one always-on preset, merged into each ledger's own advanced filters.
  const globalPreset = useMemo(() => ({
    dateFrom: effectiveWindow.from,
    dateTo: effectiveWindow.to,
    project: gProjectId || "all",
    projectId: gProjectId || "all",
    department: catDrill?.department || "all",
    category: gCategory || "all",
    subCategory: gSubCategory || "all",
    serviceType: gServiceType || "all",
    submittedBy: gPerson || "all",
  }), [effectiveWindow.from, effectiveWindow.to, gProjectId, catDrill, gCategory, gSubCategory, gServiceType, gPerson]);

  useEffect(() => { onFilterChange(globalPreset); }, [globalPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  const anyGlobalActive = !!(gProjectId || gPerson || gCategory || gSubCategory || gServiceType || drillRange || catDrill);
  const clearGlobalFilters = () => { setGProjectId(""); setGPerson(""); setGCategory(""); setGSubCategory(""); setGServiceType(""); setDrillRange(null); setCatDrill(null); };

  const goToBucket = (point) => {
    if (!point?.from) return;
    setDrillRange({ from: point.from, to: point.to, label: point.label });
    onNavigate(activeGraphMode.ledgerTab, null);
  };

  // Outstanding / Received Drilldown — clicking a slice opens only that
  // period's invoices in that state (amber → sent/overdue, green → received/paid).
  const goToStatusSlice = (point, statuses, label) => {
    if (!point?.from) return;
    setDrillRange({ from: point.from, to: point.to, label: point.label });
    onNavigate("revenue-ledger", { statuses, label: `${label} · ${point.label}` });
  };
  const goToOutstandingSlice = (point) => goToStatusSlice(point, OUTSTANDING_STATUSES, "Outstanding");
  const goToReceivedSlice = (point) => goToStatusSlice(point, RECEIVED_STATUSES, "Received");

  const goToProjectBar = (projectId, seriesKey) => {
    if (projectId && projectId !== gProjectId) setGProjectId(projectId);
    onNavigate(seriesKey.startsWith("Expense") ? "expenses-ledger" : "revenue-ledger", null);
  };

  // Feb 2026 — Personnel Burn is now a per-person bar chart; clicking a
  // bar drills into that (normalized) person's expenses, mirroring how
  // clicking a Project Profitability bar drills into that project.
  const goToPersonBar = (personLabel) => {
    setGPerson(personLabel);
    onNavigate("expenses-ledger", null);
  };

  // Interactive Sub-category Stacking — clicking an individual STACK
  // SEGMENT (a slice of a bar, not the whole bar) narrows the ledger down
  // to that exact segment, on top of whatever the bar itself represents.
  const goToStackSegment = (point, seriesType, segmentValue) => {
    if (seriesType === "revenue") setGServiceType(segmentValue === "Unspecified" ? "" : segmentValue);
    else setGSubCategory(segmentValue === "Uncategorized" ? "" : segmentValue);
    if (mode === "revenue" || mode === "expense") {
      if (point?.from) setDrillRange({ from: point.from, to: point.to, label: point.label });
      onNavigate(activeGraphMode.ledgerTab, null);
    } else if (mode === "personnel") {
      setGPerson(point.label);
      onNavigate("expenses-ledger", null);
    }
  };

  // Project Profitability stacked segment click — same project drill as
  // goToProjectBar, plus the specific Service Type / Sub-category slice.
  const goToProfitSegment = (projectId, seriesType, segmentValue) => {
    if (projectId && projectId !== gProjectId) setGProjectId(projectId);
    if (seriesType === "revenue") { setGServiceType(segmentValue === "Unspecified" ? "" : segmentValue); onNavigate("revenue-ledger", null); }
    else { setGSubCategory(segmentValue === "Uncategorized" ? "" : segmentValue); onNavigate("expenses-ledger", null); }
  };

  // Current mode's active stack segments (Category Burn is intentionally
  // excluded — its own drill-down already IS the breakdown).
  const currentStackSegments = mode === "revenue" ? revenueStackSegments : mode === "expense" ? expenseStackSegments : mode === "personnel" ? personnelStackSegments : [];
  const isCurrentModeStacked = currentStackSegments.length > 0;
  const isProfitStacked = mode === "profitability" && (profitRevenueSegments.length > 0 || profitExpenseSegments.length > 0);

  // Auto-Scaling Y-Axis — always 15% of headroom above the tallest bar
  // (or tallest STACKED total) so the ₹-total label above it never clips
  // against the top of the chart.
  const chartMaxValue = useMemo(() => {
    if (!graphData.length) return 0;
    if (mode === "profitability") {
      return Math.max(...graphData.map((r) => Math.max(r.Revenue || 0, r.Expense || 0, showYoY ? (r.RevenueLY || 0) : 0, showYoY ? (r.ExpenseLY || 0) : 0)));
    }
    const seriesKey = activeGraphMode.series;
    return Math.max(...graphData.map((r) => Math.max(r._stackTotal ?? r[seriesKey] ?? 0, showYoY ? (r[`${seriesKey}LY`] || 0) : 0)));
  }, [graphData, mode, activeGraphMode, showYoY]);
  const yAxisDomain = chartMaxValue > 0 ? [0, Math.ceil(chartMaxValue * 1.15)] : [0, 'auto'];

  return (
    <>
      {/* Master Dynamic Analytics Engine */}
      <div className="card-flat p-4 mb-6" data-testid="finance-analytics-panel">
        {/* Master mode toggles */}
        <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="finance-mode-toggle">
          {GRAPH_MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`px-3 py-2 rounded-sm text-xs font-medium inline-flex items-center gap-1.5 transition-colors border ${mode === m.id ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              data-testid={`finance-mode-${m.id}`}
            >
              <m.icon size={13} />{m.label}
            </button>
          ))}
        </div>

        {/* Time Navigator + Master filter bar */}
        <div className="flex items-center gap-3 flex-wrap mb-2" data-testid="finance-global-filter-bar">
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden bg-white" data-testid="finance-granularity-toggle">
            {PERIOD_GRANULARITIES.map((g) => (
              <button
                key={g.id}
                onClick={() => setGranularity(g.id)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${granularity === g.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid={`finance-granularity-${g.id}`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <Select value={gProjectId || "all"} onValueChange={(v) => setGProjectId(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-40 bg-white" data-testid="finance-global-filter-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projectsList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {mode === "revenue" && (
            <Select value={gServiceType || "all"} onValueChange={(v) => setGServiceType(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-sm h-8 text-xs w-44 bg-white" data-testid="finance-filter-service-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All service types</SelectItem>
                {serviceTypeChoices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {mode === "expense" && (
            <>
              <Select value={gCategory || "all"} onValueChange={(v) => { setGCategory(v === "all" ? "" : v); setGSubCategory(""); }}>
                <SelectTrigger className="rounded-sm h-8 text-xs w-40 bg-white" data-testid="finance-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categoryChoices.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={gSubCategory || "all"} onValueChange={(v) => setGSubCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-8 text-xs w-44 bg-white" data-testid="finance-filter-subcategory"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sub-categories</SelectItem>
                  {subCategoryChoices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {mode === "personnel" && (
            <span className="text-[11px] text-slate-400 italic">Click any person's bar below to drill into their expenses.</span>
          )}

          {anyGlobalActive && (
            <button onClick={clearGlobalFilters} className="text-[11px] text-[#F54800] hover:underline ml-auto inline-flex items-center gap-1" data-testid="finance-global-filter-clear">
              <X size={11} /> Clear filters{drillRange ? ` (${drillRange.label})` : ""}
            </button>
          )}
        </div>

        {/* MoM / QoQ Time Navigator — only for Monthly / Quarterly granularity */}
        {granularity === "monthly" && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[11px] text-slate-500">MoM:</span>
            <Select value={pickedMonth} onValueChange={setPickedMonth}>
              <SelectTrigger className="rounded-sm h-8 text-xs w-40 bg-white" data-testid="finance-mom-picker"><SelectValue /></SelectTrigger>
              <SelectContent>{monthPickerOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {granularity === "quarterly" && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[11px] text-slate-500">QoQ:</span>
            <Select value={pickedQuarter} onValueChange={setPickedQuarter}>
              <SelectTrigger className="rounded-sm h-8 text-xs w-40 bg-white" data-testid="finance-qoq-picker"><SelectValue /></SelectTrigger>
              <SelectContent>{quarterPickerOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        {/* Saved Finance Views */}
        <div className="mb-5 pb-3 border-b border-slate-100">
          <SavedViewsRow
            apiPath="/users/me/saved-finance-views"
            testIdPrefix="saved-finance-view"
            currentFilters={{
              mode, granularity, picked_month: pickedMonth, picked_quarter: pickedQuarter,
              project_id: gProjectId || null, person: gPerson || null, category: gCategory || null,
              sub_category: gSubCategory || null, service_type: gServiceType || null,
            }}
            onApply={(v) => {
              setMode(v.mode || "revenue");
              setGranularity(v.granularity || "monthly");
              setPickedMonth(v.picked_month || currentMonthValue());
              setPickedQuarter(v.picked_quarter || currentQuarterValue());
              setGProjectId(v.project_id || "");
              setGPerson(v.person || "");
              setGCategory(v.category || "");
              setGSubCategory(v.sub_category || "");
              setGServiceType(v.service_type || "");
              setDrillRange(null);
            }}
          />
        </div>

        {/* The one singular full-width dynamic graph */}
        <div className="card-flat p-4 mb-4" data-testid="finance-trend-chart">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1.5">
              <activeGraphMode.icon size={14} className="text-[#F54800]" />{activeGraphMode.label}
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="finance-yoy-toggle">
              <Switch checked={showYoY} onCheckedChange={setShowYoY} data-testid="finance-yoy-switch" />
              Compare to last year
            </label>
          </div>
          {mode === "category_burn" && (
            <div className="flex items-center gap-2 mb-2 text-xs" data-testid="category-burn-breadcrumb">
              {catDrill && (
                <button onClick={() => setCatLevel(catDrill.category ? { department: catDrill.department } : null)} className="inline-flex items-center gap-0.5 font-medium text-[#F54800] hover:underline" data-testid="category-burn-back-btn">
                  <ChevronLeft size={13} /> Back
                </button>
              )}
              <button onClick={() => setCatLevel(null)} className={`hover:underline ${!catDrill ? "font-semibold text-slate-800" : "text-slate-500"}`} data-testid="category-burn-breadcrumb-root">Finance</button>
              {catDrill && (
                <>
                  <span className="text-slate-400">/</span>
                  <button onClick={() => setCatLevel({ department: catDrill.department })} className={!catDrill.category ? "font-semibold text-slate-800" : "text-slate-500 hover:underline"} data-testid="category-burn-breadcrumb-department">{catDrill.department}</button>
                </>
              )}
              {catDrill?.category && (
                <>
                  <span className="text-slate-400">/</span>
                  <span className="font-semibold text-slate-800" data-testid="category-burn-breadcrumb-category">{catDrill.category}</span>
                </>
              )}
            </div>
          )}
          <div className="text-xs text-slate-500 mb-3">
            {mode === "profitability"
              ? "Revenue vs. Expense per project, for the selected period — click a bar to drill into that project's ledger."
              : mode === "personnel"
              ? "Total spend per person for the selected period — Internal Team Members only (vendors & external entities excluded); names normalized so case & spacing don't split the same person into two bars — click a bar to drill into their expenses."
              : mode === "category_burn"
              ? (catDrill?.category
                  ? `Sub-category breakdown for ${catDrill.department} / ${catDrill.category} — click a bar to filter the Expenses ledger below.`
                  : catDrill?.department
                  ? `Category breakdown for ${catDrill.department} — click a bar to drill into its sub-categories.`
                  : "Department-level spend for the selected period — click a bar to drill into its categories.")
              : `${PERIOD_GRANULARITIES.find((g) => g.id === granularity)?.label} view · click a bar to drill into that period's ledger${mode === "revenue" && !isCurrentModeStacked ? " · green slice = received invoices only, amber slice = outstanding only" : ""}.`}
            {(isCurrentModeStacked || isProfitStacked) && (
              <span className="ml-1.5 inline-flex items-center gap-1 text-[#F54800] font-medium" data-testid="finance-stacking-indicator">
                · Stacked by {mode === "profitability" ? "Service Type (Revenue) + Sub-category (Expense)" : mode === "revenue" ? "Service Type" : "Sub-category"} — click a slice to filter.
              </span>
            )}
          </div>
          {(mode === "expense" || mode === "personnel" || mode === "category_burn") && undatedExpenseCount > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5 mb-3" data-testid="finance-undated-expense-warning">
              Warning: {undatedExpenseCount} undated expense{undatedExpenseCount === 1 ? "" : "s"} found — excluded from this chart.
            </div>
          )}
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={graphData} margin={{ left: 8, right: 16, top: 24 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" fontSize={11} interval={0} angle={(mode === "profitability" || mode === "personnel" || mode === "category_burn") && graphData.length > 6 ? -20 : 0} textAnchor={(mode === "profitability" || mode === "personnel" || mode === "category_burn") && graphData.length > 6 ? "end" : "middle"} height={(mode === "profitability" || mode === "personnel" || mode === "category_burn") && graphData.length > 6 ? 60 : 30} />
              {/* Auto-Scaling Y-Axis: always 15% headroom above the tallest bar/stack so the ₹-total label never clips at the top. */}
              <YAxis fontSize={11} tickFormatter={(v) => fmtLakhs(v)} domain={yAxisDomain} allowDataOverflow={false} />
              <Tooltip formatter={(v) => fmtLakhs(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {mode === "profitability" ? (
                isProfitStacked ? (
                  <>
                    {profitRevenueSegments.map((seg, idx) => (
                      <Bar key={`rev__${seg}`} dataKey={`rev__${seg}`} name={seg} stackId="rev" fill={colorForSegment(seg)} radius={idx === profitRevenueSegments.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} style={{ cursor: "pointer" }} data-testid="finance-graph-bar-revenue-segment" onClick={(d) => goToProfitSegment(d.key, "revenue", seg)}>
                        {idx === profitRevenueSegments.length - 1 && <LabelList dataKey="Revenue" position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />}
                      </Bar>
                    ))}
                    {profitExpenseSegments.map((seg, idx) => (
                      <Bar key={`exp__${seg}`} dataKey={`exp__${seg}`} name={seg} stackId="exp" fill={colorForSegment(seg)} fillOpacity={0.75} radius={idx === profitExpenseSegments.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} style={{ cursor: "pointer" }} data-testid="finance-graph-bar-expense-segment" onClick={(d) => goToProfitSegment(d.key, "expense", seg)}>
                        {idx === profitExpenseSegments.length - 1 && <LabelList dataKey="Expense" position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />}
                      </Bar>
                    ))}
                  </>
                ) : (
                  <>
                    <Bar dataKey="Revenue" fill="#22C55E" radius={[4, 4, 0, 0]} data-testid="finance-graph-bar-revenue" style={{ cursor: "pointer" }} onClick={(d) => goToProjectBar(d.key, "Revenue")}>
                      <LabelList dataKey="Revenue" position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />
                    </Bar>
                    <Bar dataKey="Expense" fill="#F54800" radius={[4, 4, 0, 0]} data-testid="finance-graph-bar-expense" style={{ cursor: "pointer" }} onClick={(d) => goToProjectBar(d.key, "Expense")}>
                      <LabelList dataKey="Expense" position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />
                    </Bar>
                    {showYoY && <Bar dataKey="RevenueLY" name="Revenue (LY)" fill="#22C55E" fillOpacity={0.4} radius={[4, 4, 0, 0]} />}
                    {showYoY && <Bar dataKey="ExpenseLY" name="Expense (LY)" fill="#F54800" fillOpacity={0.4} radius={[4, 4, 0, 0]} />}
                  </>
                )
              ) : isCurrentModeStacked ? (
                currentStackSegments.map((seg, idx) => (
                  <Bar key={seg} dataKey={seg} name={seg} stackId="stack" fill={colorForSegment(seg)} radius={idx === currentStackSegments.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} style={{ cursor: "pointer" }} data-testid="finance-graph-bar-segment" onClick={(d) => goToStackSegment(d, mode === "revenue" ? "revenue" : "expense", seg)}>
                    {idx === currentStackSegments.length - 1 && <LabelList dataKey="_stackTotal" position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />}
                  </Bar>
                ))
              ) : mode === "revenue" ? (
                <>
                  <Bar dataKey="Revenue" name="Revenue (Received)" fill={RECEIVED_COLOR} radius={[4, 4, 0, 0]} data-testid="finance-graph-bar-received" style={{ cursor: "pointer" }} onClick={(d) => goToReceivedSlice(d)}>
                    <LabelList dataKey="Revenue" position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />
                  </Bar>
                  <Bar dataKey="Outstanding" name="Outstanding" fill={OUTSTANDING_COLOR} radius={[4, 4, 0, 0]} data-testid="finance-graph-bar-outstanding" style={{ cursor: "pointer" }} onClick={(d) => goToOutstandingSlice(d)}>
                    <LabelList dataKey="Outstanding" position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />
                  </Bar>
                  {showYoY && <Bar dataKey="RevenueLY" name="Revenue (LY)" fill={RECEIVED_COLOR} fillOpacity={0.4} radius={[4, 4, 0, 0]} />}
                </>
              ) : (
                <>
                  <Bar dataKey={activeGraphMode.series} fill={activeGraphMode.color} radius={[4, 4, 0, 0]} data-testid="finance-graph-bar" style={{ cursor: "pointer" }} onClick={(d) => (mode === "personnel" ? goToPersonBar(d.label) : mode === "category_burn" ? goToCategoryBar(d) : goToBucket(d))}>
                    <LabelList dataKey={activeGraphMode.series} position="top" formatter={(v) => fmtLakhs(v)} style={{ fontSize: 9, fill: "#475569" }} />
                  </Bar>
                  {showYoY && <Bar dataKey={`${activeGraphMode.series}LY`} name={`${activeGraphMode.series} (LY)`} fill={activeGraphMode.color} fillOpacity={0.4} radius={[4, 4, 0, 0]} />}
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
          {graphData.length === 0 && <div className="text-xs text-slate-400 italic py-4 text-center">No data in this window yet.</div>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <button className="text-left" onClick={() => onNavigate("revenue-ledger", { statuses: [...OUTSTANDING_STATUSES, "received", "paid", "draft", "cancelled"], label: "All invoices this period" })} data-testid="finance-stat-income">
          <StatCard label="Revenue (Received)" value={fmt(totals.received)} hint={`${totals.invoiceCount} invoice(s) this period`} />
        </button>
        <button className="text-left" onClick={() => onNavigate("revenue-ledger", { statuses: ["received", "paid"], label: "Received invoices" })} data-testid="finance-stat-received">
          <StatCard label="Received" value={fmt(totals.received)} hint={`${totals.receivedCount} invoice(s)`} accent="text-green-700" />
        </button>
        <button className="text-left" onClick={() => onNavigate("revenue-ledger", { statuses: OUTSTANDING_STATUSES, label: "Outstanding invoices" })} data-testid="finance-stat-outstanding">
          <StatCard label="Outstanding" value={fmt(totals.outstanding)} hint={`${totals.outstandingCount} invoice(s)`} accent="text-orange-700" />
        </button>
        <button className="text-left" onClick={() => onNavigate("expenses-ledger", { tab: "expenses", label: "Expenses this period" })} data-testid="finance-stat-net">
          <StatCard label="Expenses" value={fmt(totals.expense)} hint={`Net ${fmt(totals.net)}`} />
        </button>
      </div>
    </>
  );
}
